import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  safetyContactsAdminService,
  SafetyContactInput,
  SafetyContactRow,
} from '../services/safetyContactsAdminService';

// FEAT-044 admin surface (MVP-8.10a).
// Lets admins curate per-zip non-emergency police + APS numbers. Supports
// row edit/delete plus bulk CSV import (zip,nonEmergencyPolice,aps,regionalNotes).
// The user-facing tone is intentionally calm — this admin page can be
// pragmatic and operational, but the data it produces feeds the calm Safety
// surface on web + mobile.

interface EditDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  initial: SafetyContactInput | null;
}

const EMPTY_INPUT: SafetyContactInput = {
  zip: '',
  nonEmergencyPolice: '',
  aps: '',
  regionalNotes: '',
};

const ZIP_PATTERN = /^\d{5}$/;

export const SafetyContactsAdminPage: React.FC = () => {
  const [rows, setRows] = React.useState<SafetyContactRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [editDialog, setEditDialog] = React.useState<EditDialogState>({
    open: false,
    mode: 'create',
    initial: null,
  });
  const [draft, setDraft] = React.useState<SafetyContactInput>(EMPTY_INPUT);
  const [saving, setSaving] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const loadRows = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await safetyContactsAdminService.list();
      setRows(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load safety contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const openCreate = () => {
    setDraft(EMPTY_INPUT);
    setEditDialog({ open: true, mode: 'create', initial: null });
  };

  const openEdit = (row: SafetyContactRow) => {
    const initial: SafetyContactInput = {
      zip: row.zip,
      nonEmergencyPolice: row.nonEmergencyPolice,
      aps: row.aps,
      regionalNotes: row.regionalNotes ?? '',
    };
    setDraft(initial);
    setEditDialog({ open: true, mode: 'edit', initial });
  };

  const closeDialog = () => {
    setEditDialog({ open: false, mode: 'create', initial: null });
    setSaving(false);
  };

  const handleSave = async () => {
    if (!ZIP_PATTERN.test(draft.zip.trim())) {
      setError('Zip must be a 5-digit US zip code.');
      return;
    }
    if (!draft.nonEmergencyPolice.trim() || !draft.aps.trim()) {
      setError('Both non-emergency police and APS numbers are required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const contacts: SafetyContactInput[] = [
        {
          zip: draft.zip.trim(),
          nonEmergencyPolice: draft.nonEmergencyPolice.trim(),
          aps: draft.aps.trim(),
          regionalNotes: draft.regionalNotes?.trim() || undefined,
        },
      ];
      await safetyContactsAdminService.bulkUpsert(contacts);
      setSuccess(
        editDialog.mode === 'create'
          ? `Added safety contacts for zip ${draft.zip}.`
          : `Updated safety contacts for zip ${draft.zip}.`
      );
      closeDialog();
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save safety contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: SafetyContactRow) => {
    if (!window.confirm(`Remove safety contacts for zip ${row.zip}? This cannot be undone.`)) {
      return;
    }
    setError(null);
    try {
      await safetyContactsAdminService.remove(row.zip);
      setSuccess(`Removed safety contacts for zip ${row.zip}.`);
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove safety contact');
    }
  };

  const handleCsvImport: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setError(null);
    try {
      const text = await file.text();
      await safetyContactsAdminService.uploadCsv(text);
      setSuccess(`Imported safety contacts from ${file.name}.`);
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSV import failed');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Safety Contacts Admin
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage per-zip non-emergency police and Adult Protective Services
            numbers. Unmapped zips fall back to United Way 211 on the
            user-facing Safety surface.
          </Typography>
        </Box>

        {error ? (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        ) : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            data-testid="safety-contacts-admin-add"
          >
            Add zip mapping
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            data-testid="safety-contacts-admin-csv"
          >
            Import CSV
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleCsvImport}
            aria-label="Upload safety contacts CSV"
            data-testid="safety-contacts-admin-csv-input"
          />
          <Button
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => void loadRows()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        <Paper variant="outlined">
          {loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" aria-label="Safety contacts mapping table">
                <TableHead>
                  <TableRow>
                    <TableCell>Zip</TableCell>
                    <TableCell>Non-emergency police</TableCell>
                    <TableCell>APS</TableCell>
                    <TableCell>Regional notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 3, textAlign: 'center' }}
                        >
                          No safety contacts on file yet. Add a zip mapping or
                          import a CSV to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.zip} hover>
                        <TableCell>{row.zip}</TableCell>
                        <TableCell>{row.nonEmergencyPolice}</TableCell>
                        <TableCell>{row.aps}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          {row.regionalNotes ?? '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openEdit(row)}
                              aria-label={`Edit safety contacts for zip ${row.zip}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => void handleDelete(row)}
                              aria-label={`Delete safety contacts for zip ${row.zip}`}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog open={editDialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {editDialog.mode === 'create' ? 'Add safety contacts' : 'Edit safety contacts'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="US zip code"
              value={draft.zip}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, zip: event.target.value }))
              }
              disabled={editDialog.mode === 'edit'}
              inputProps={{
                inputMode: 'numeric',
                pattern: '\\d{5}',
                maxLength: 5,
                'data-testid': 'safety-contacts-admin-zip',
              }}
              fullWidth
            />
            <TextField
              label="Non-emergency police number"
              value={draft.nonEmergencyPolice}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, nonEmergencyPolice: event.target.value }))
              }
              inputProps={{ 'data-testid': 'safety-contacts-admin-police' }}
              fullWidth
            />
            <TextField
              label="Adult Protective Services number"
              value={draft.aps}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, aps: event.target.value }))
              }
              inputProps={{ 'data-testid': 'safety-contacts-admin-aps' }}
              fullWidth
            />
            <TextField
              label="Regional notes (optional)"
              value={draft.regionalNotes ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, regionalNotes: event.target.value }))
              }
              inputProps={{ maxLength: 500 }}
              multiline
              minRows={2}
              fullWidth
              helperText="Short context for the support team. Visible to users on the Safety page."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            data-testid="safety-contacts-admin-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SafetyContactsAdminPage;
